import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, Container, Paper, Typography, Grid, TextField, Chip, Stack, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem
} from '@mui/material';
import { listServicos, createServico, updateServico, deleteServico } from '../../services/servicos';

// Mantemos categorias apenas como filtros visuais opcionais (não aplicados quando não há categoria no backend)
const CATEGORIAS = [];

const TiposServicos = () => {
  const [q, setQ] = useState('');
  const [categoria, setCategoria] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [itens, setItens] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ tipo_servico: '', descricao: '', status: 'Ativo', valor_pequeno: '', valor_medio: '', valor_grande: '' });
  const [editForm, setEditForm] = useState({ id: '', nome: '', descricao: '', status: 'Ativo', valor: '', tempo_medio_minutos: '', data_criacao: '', data_atualizacao: '' });

  const loadServicos = async () => {
    try {
      setLoading(true);
      const response = await listServicos();
      const data = response.data || response.servicos || response;
      setItens(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar serviços', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServicos();
  }, []);

  const openCreate = () => setCreateOpen(true);
  const closeCreate = () => setCreateOpen(false);
  const onChangeForm = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };
  const openEdit = (item) => {
    setEditForm({
      id: item.id,
      nome: item.tipo_servico || item.nome || '', // Mantém compatibilidade
      descricao: item.descricao || '',
      status: item.ativo ? 'Ativo' : 'Inativo',
      valor: item.valor ?? '',
      tempo_medio_minutos: item.tempo_medio_minutos ?? '',
      data_criacao: item.data_criacao || '',
      data_atualizacao: item.data_atualizacao || ''
    });
    setEditOpen(true);
  };
  const closeEdit = () => setEditOpen(false);
  const onChangeEditForm = (e) => {
    const { name, value } = e.target;
    setEditForm((p) => ({ ...p, [name]: value }));
  };
  const saveServico = async () => {
    try {
      setLoading(true);
      await createServico(form);
      setForm({ tipo_servico: '', descricao: '', status: 'Ativo', valor_pequeno: '', valor_medio: '', valor_grande: '' });
      closeCreate();
      await loadServicos();
    } catch (e) {
      console.error('Erro ao criar serviço', e);
    } finally {
      setLoading(false);
    }
  };
  const updateServicoSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        nome: editForm.nome,
        descricao: editForm.descricao,
        ativo: editForm.status === 'Ativo',
        valor: editForm.valor === '' ? null : Number(editForm.valor),
        tempo_medio_minutos: editForm.tempo_medio_minutos === '' ? null : Number(editForm.tempo_medio_minutos),
      };
      await updateServico(editForm.id, payload);
      closeEdit();
      await loadServicos();
    } catch (e) {
      console.error('Erro ao atualizar serviço', e);
    } finally {
      setLoading(false);
    }
  };
  const deleteServicoSubmit = async () => {
    if (!editForm.id) return;
    if (!window.confirm('Confirmar exclusão deste serviço?')) return;
    try {
      setLoading(true);
      await deleteServico(editForm.id);
      closeEdit();
      await loadServicos();
    } catch (e) {
      console.error('Erro ao excluir serviço', e);
    } finally {
      setLoading(false);
    }
  };

  const filtrados = useMemo(() => {
    const text = q.toLowerCase();
    return itens.filter((t) => {
      const titulo = t.tipo_servico || '';
      const desc = t.descricao || '';
      const matchTexto = `${titulo} ${desc}`.toLowerCase().includes(text);
      // Sem categoria no backend, ignoramos filtro de categoria
      return matchTexto;
    });
  }, [q, itens]);

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" component="h1">Nossos Serviços</Typography>
          <Typography variant="body1" color="text.secondary">
            Lista e descrição dos serviços oferecidos. Ajustaremos para refletir o site atual.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>Novo Serviço</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField 
              fullWidth 
              size="small" 
              label="Buscar serviços" 
              placeholder="Pesquisar por nome ou descrição"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Grid>
          {CATEGORIAS.length > 0 && (
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip 
                  label="Todos" 
                  color={categoria === 'todos' ? 'primary' : 'default'}
                  onClick={() => setCategoria('todos')}
                  variant={categoria === 'todos' ? 'filled' : 'outlined'}
                />
                {CATEGORIAS.map((c) => (
                  <Chip 
                    key={c} 
                    label={c.charAt(0).toUpperCase() + c.slice(1)} 
                    color={categoria === c ? 'primary' : 'default'}
                    onClick={() => setCategoria(c)}
                    variant={categoria === c ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {loading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Carregando...</Typography>
            </Paper>
          </Grid>
        )}
        {!loading && filtrados.map((s) => (
          <Grid key={s.id} item xs={12} md={6} lg={4}>
            <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" component="h2">{s.tipo_servico}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">{s.descricao}</Typography>
                <Box sx={{ pt: 1 }}>
                  <Button size="small" variant="text" onClick={() => openEdit(s)}>Editar</Button>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
        {!loading && filtrados.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Nenhum serviço encontrado.</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      {/* Dialog de edição */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Editar Serviço</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nome do Serviço" name="nome" value={editForm.nome} onChange={onChangeEditForm} fullWidth size="small" required />
            <TextField label="Descrição" name="descricao" value={editForm.descricao} onChange={onChangeEditForm} fullWidth size="small" multiline minRows={3} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField type="number" inputProps={{ step: '0.01', min: 0 }} label="Valor" name="valor" value={editForm.valor} onChange={onChangeEditForm} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField type="number" inputProps={{ step: '1', min: 0 }} label="Tempo (minutos)" name="tempo_medio_minutos" value={editForm.tempo_medio_minutos} onChange={onChangeEditForm} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select label="Status" name="status" value={editForm.status} onChange={onChangeEditForm} size="small" fullWidth>
                  <MenuItem value="Ativo">Ativo</MenuItem>
                  <MenuItem value="Inativo">Inativo</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={deleteServicoSubmit} disabled={loading}>Excluir</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeEdit}>Cancelar</Button>
          <Button onClick={updateServicoSubmit} variant="contained" disabled={loading}>{loading ? 'Salvando...' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de novo serviço */}
      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>Novo Serviço</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Tipo de Serviço" name="tipo_servico" value={form.tipo_servico} onChange={onChangeForm} fullWidth size="small" required />
            <TextField label="Descrição" name="descricao" value={form.descricao} onChange={onChangeForm} fullWidth size="small" multiline minRows={3} />
            <TextField select label="Status" name="status" value={form.status} onChange={onChangeForm} size="small">
              <MenuItem value="Ativo">Ativo</MenuItem>
              <MenuItem value="Inativo">Inativo</MenuItem>
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField type="number" inputProps={{ step: '0.01', min: 0 }} label="Valor (até 30)" name="valor_pequeno" value={form.valor_pequeno} onChange={onChangeForm} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField type="number" inputProps={{ step: '0.01', min: 0 }} label="Valor (31 a 50)" name="valor_medio" value={form.valor_medio} onChange={onChangeForm} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField type="number" inputProps={{ step: '0.01', min: 0 }} label="Valor (51 a 70)" name="valor_grande" value={form.valor_grande} onChange={onChangeForm} fullWidth size="small" />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate}>Cancelar</Button>
          <Button onClick={saveServico} variant="contained" disabled={loading}>{loading ? 'Salvando...' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default TiposServicos;
